import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaSearch, FaFilter, FaPills, FaBrain, FaHeart, FaBolt, FaLeaf, FaShieldAlt } from 'react-icons/fa';
import './Products.css';

const Products = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const products = [
    // NEUROPATHIC PAIN
    {
      name: 'LINREGA-NT',
      category: 'Neuropathic Pain',
      description: 'Pregabalin 75mg + Nortriptyline 10mg Tablet for neuropathic pain management',
      color: '#E67E22',
      indication: 'Neuropathic Pain, Diabetic Neuropathy, Post-herpetic neuralgia',
      strength: 'Pregabalin 75mg + Nortriptyline 10mg',
      packSize: '10x10 Tablets'
    },
    {
      name: 'GABAPROVEN NT 100',
      category: 'Neuropathic Pain',
      description: 'Gabapentin 100mg + Nortriptyline 10mg Tablet for nerve pain relief',
      color: '#3498DB',
      indication: 'Neuropathic Pain, Seizures, Anxiety',
      strength: 'Gabapentin 100mg + Nortriptyline 10mg',
      packSize: '10x10 Tablets'
    },
    {
      name: 'GABAPROVEN - ER 450MG',
      category: 'Neuropathic Pain',
      description: 'Gabapentin 450mg Extended Release Tablet for sustained pain control',
      color: '#2C3E50',
      indication: 'Chronic Neuropathic Pain, Epilepsy',
      strength: 'Gabapentin 450mg ER',
      packSize: '10x10 Tablets'
    },
    // PAIN MANAGEMENT NSAID / MUSCLE RELAXANTS
    {
      name: 'TOLTRICE AP',
      category: 'Pain Management',
      description: 'Tolperisone 150mg + Aceclofenac 100mg + Paracetamol 325mg Tablet',
      color: '#E91E63',
      indication: 'Muscle Spasm, Inflammatory Pain, Fever',
      strength: 'Tolperisone 150mg + Aceclofenac 100mg + Paracetamol 325mg',
      packSize: '10x10 Tablets'
    },
    {
      name: 'PERISOON 50MG',
      category: 'Pain Management',
      description: 'Eperisone 50mg Tablet for muscle relaxation and pain relief',
      color: '#9B59B6',
      indication: 'Muscle Spasticity, Back Pain, Neck Pain',
      strength: 'Eperisone 50mg',
      packSize: '10x10 Tablets'
    },
    // IN MIGRAINE / IN VERTIGO
    {
      name: 'FILNARZYN PLUS',
      category: 'Migraine & Vertigo',
      description: 'Flunarizine 10mg + Propranolol 40mg SR for migraine prevention',
      color: '#2C3E50',
      indication: 'Migraine Prevention, Vertigo, Dizziness',
      strength: 'Flunarizine 10mg + Propranolol 40mg SR',
      packSize: '10x10 Tablets'
    },
    {
      name: 'VERTILEAVE - 8MG / 16MG',
      category: 'Migraine & Vertigo',
      description: 'Betahistine 8mg / 16mg Tablet for vertigo and balance disorders',
      color: '#E74C3C',
      indication: 'Vertigo, Meniere\'s Disease, Balance Disorders',
      strength: 'Betahistine 8mg / 16mg',
      packSize: '10x10 Tablets'
    },
    // VITAMIN - FOR NEUROPATHY/ IMMUNE POWER
    {
      name: 'NEUFOBEN PLUS',
      category: 'Vitamins & Neuropathy',
      description: 'Alphalipoicacid + Methylcobalamin + Vitamin B6 + Folic acid + Benfothiamine + Biotin + Chromium Capsules',
      color: '#C0392B',
      indication: 'Diabetic Neuropathy, Vitamin B Deficiency, Oxidative Stress',
      strength: 'Multi-vitamin Complex',
      packSize: '10x10 Capsules'
    },
    {
      name: 'VITALIKE PLUS',
      category: 'Vitamins & Neuropathy',
      description: 'Multivitamins + Multiminerals + Antioxidant + Aminoacids with Taurine 50mg Tablet',
      color: '#8E44AD',
      indication: 'Nutritional Deficiency, Immune Support, General Wellness',
      strength: 'Multivitamin Complex with Taurine 50mg',
      packSize: '10x10 Tablets'
    },
    // PPI's
    {
      name: 'EASEZOLE 40MG',
      category: 'PPIs',
      description: 'Esomeprazole 40mg Tablet for acid-related disorders',
      color: '#3498DB',
      indication: 'GERD, Peptic Ulcers, Acid Reflux',
      strength: 'Esomeprazole 40mg',
      packSize: '10x10 Tablets'
    },
    {
      name: 'RABEROLE DSR',
      category: 'PPIs',
      description: 'Rabeprazole 20mg EC + Domperidone 30 SR for gastric disorders',
      color: '#E74C3C',
      indication: 'GERD, Dyspepsia, Gastroesophageal Reflux',
      strength: 'Rabeprazole 20mg EC + Domperidone 30 SR',
      packSize: '10x10 Tablets'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Products', icon: FaPills },
    { id: 'Neuropathic Pain', name: 'Neuropathic Pain', icon: FaBrain },
    { id: 'Pain Management', name: 'Pain Management', icon: FaHeart },
    { id: 'Migraine & Vertigo', name: 'Migraine & Vertigo', icon: FaBolt },
    { id: 'Vitamins & Neuropathy', name: 'Vitamins & Neuropathy', icon: FaLeaf },
    { id: 'PPIs', name: 'PPIs', icon: FaShieldAlt }
  ];

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="products-page">
      {/* Hero Section */}
      <section className="products-hero">
        <div className="products-hero-background">
          <div className="products-hero-overlay"></div>
        </div>
        <div className="container">
          <motion.div
            className="products-hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="products-hero-title">Our Product Portfolio</h1>
            <p className="products-hero-subtitle">
              Comprehensive pharmaceutical solutions for Neurology, Psychiatry, and Pain Management
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="products-filters section">
        <div className="container">
          <div className="filters-header">
            <motion.div
              className="search-box"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </motion.div>

            <motion.div
              className="filter-info"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <FaFilter className="filter-icon" />
              <span>{filteredProducts.length} Products Found</span>
            </motion.div>
          </div>

          <motion.div
            className="category-filters"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {categories.map((category) => (
              <button
                key={category.id}
                className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <category.icon className="category-icon" />
                {category.name}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Products Grid Section */}
      <section className="products-grid-section section">
        <div className="container">
          <motion.div
            className="products-grid"
            layout
          >
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.name}
                className="product-card"
                style={{ '--product-color': product.color }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                layout
              >
                <div className="product-header">
                  <div className="product-category-badge">
                    {product.category}
                  </div>
                  <div className="product-icon">
                    <FaPills />
                  </div>
                </div>

                <div className="product-body">
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-description">{product.description}</p>
                  
                  <div className="product-details">
                    <div className="detail-item">
                      <span className="detail-label">Indication:</span>
                      <span className="detail-value">{product.indication}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Strength:</span>
                      <span className="detail-value">{product.strength}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Pack Size:</span>
                      <span className="detail-value">{product.packSize}</span>
                    </div>
                  </div>
                </div>

                <div className="product-footer">
                  <button className="product-btn">
                    Learn More
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {filteredProducts.length === 0 && (
            <motion.div
              className="no-products"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <FaPills className="no-products-icon" />
              <h3>No products found</h3>
              <p>Try adjusting your search criteria or category filter</p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Categories Overview */}
      <section className="categories-overview section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Our Therapeutic Areas</h2>
            <p className="section-subtitle">
              Specialized pharmaceutical solutions across critical healthcare domains
            </p>
          </motion.div>

          <div className="categories-grid">
            {categories.slice(1).map((category, index) => (
              <motion.div
                key={category.id}
                className="category-overview-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
                viewport={{ once: true }}
              >
                <div className="category-overview-icon">
                  <category.icon />
                </div>
                <h3 className="category-overview-title">{category.name}</h3>
                <p className="category-overview-count">
                  {products.filter(p => p.category === category.id).length} Products
                </p>
                <button
                  className="category-overview-btn"
                  onClick={() => setSelectedCategory(category.id)}
                >
                  Explore Products
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
